import { hideableValueCreateHidden } from "@wix/devzai-utils-common";
import type { HideableValue } from "@wix/devzai-utils-common";
import {bymoTemplateMetadataFactoryCreate} from '../../../doppe-sdk';
import type {BymoPageHasSearchProps, BymoPageSocialLinksProps} from '../../../client-server-common';
import {
    aiGenerationStrategyExclude,
    BymoPagePropsContentSettingSpecialSemanticMeaning,
    BymoPagePropsContentSettingType,
    BymoPagePropsSpecialContentSettingMetadata,
    BymoPageSocialLinksButtonStyle,
    BymoPageSocialLinksColorMode,
    BymoPageSocialLinksIconType,
    BymoPageSocialLinksSize,
    bymoPagePropsCreateContentSettingMetadata
} from '../../../client-server-common';
import {doppeActionButtonTemplateGetSocialLinksDefault} from '../../../client-server-common/doppe-templates/doppe-action-button-template-utils';
import {
    BymoTemplateSpeedDialActionsListAppearance,
    BymoTemplateSpeedDialAutoComplete,
    BymoTemplateSpeedDialHeaderAppearance,
    BymoTemplateSpeedDialSearchAppearance,
    BymoTemplateSpeedDialSocialLinksAppearance
} from './bymo-template-speed-dial-common';

export namespace BymoTemplateSpeedDial {

    export interface TemplateProps extends BymoPageSocialLinksProps , BymoPageHasSearchProps {
        autoCompleteBehaviour: BymoTemplateSpeedDialAutoComplete;
        showFullScreenAutoCompleteOnMobile: boolean;
        actionsHeading: HideableValue<string>;
        actionsListAppearance: BymoTemplateSpeedDialActionsListAppearance;
        bioText: HideableValue<string>;
        bioTextMaxLines: HideableValue<number>;
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance;
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance;
    }
}

const TemplatePropsDefaultValues = () => {
    return {
        autoCompleteBehaviour: BymoTemplateSpeedDialAutoComplete.OnInputSearch as BymoTemplateSpeedDialAutoComplete,
        showFullScreenAutoCompleteOnMobile: false,
        actionsHeading: '',
        bioText: '',
        actionsListAppearance: BymoTemplateSpeedDialActionsListAppearance.Visible as BymoTemplateSpeedDialActionsListAppearance,

        socialLinks: [],
        socialLinksIconType: BymoPageSocialLinksIconType.Default,
        socialLinksButtonStyle: BymoPageSocialLinksButtonStyle.Plain,
        socialLinksCustomActionButtonTemplate: doppeActionButtonTemplateGetSocialLinksDefault(),
        socialLinksSize: BymoPageSocialLinksSize.Medium,
        socialLinksColorMode: BymoPageSocialLinksColorMode.TextColor,
        bioTextMaxLines: hideableValueCreateHidden(3),
        searchAppearance: BymoTemplateSpeedDialSearchAppearance.Hidden ,
        searchPlaceholder: hideableValueCreateHidden(""),
        headerAppearance: BymoTemplateSpeedDialHeaderAppearance.Default ,
        socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Default ,
    } as BymoTemplateSpeedDial.TemplateProps;
}


/**
 * Speed-dial `TemplateProps` registry of property names whose runtime type is
 * `HideableValue<...>`. Mirrors action settings' `hideableContentSettingsNames`
 * and feeds the LLM schema emitter so hideable template props are rendered as
 * optional in the blueprint type (e.g. `bioText?: string`) instead of every
 * field being unconditionally marked optional.
 *
 * The `satisfies` constraint pins this to the actual hideable shape of
 * `BymoTemplateSpeedDial.TemplateProps`: any new `HideableValue<...>`-typed
 * template prop must be registered here, and any non-hideable key is rejected.
 */
export const BymoTemplateSpeedDialTemplatePropsHideableNames = {
    actionsHeading: true,
    bioText: true,
    bioTextMaxLines: true,
    searchPlaceholder: true,
} satisfies Record<HideableValue.ResolveHideableValuesKeys<BymoTemplateSpeedDial.TemplateProps>, true>;

export const BymoTemplateSpeedDialTemplateMetadata = bymoTemplateMetadataFactoryCreate<BymoTemplateSpeedDial.TemplateProps>()({
    initialValues: TemplatePropsDefaultValues(),
    resolveProps: props => {
      const {
            ...restProps
        } = props;

        return {
            ...TemplatePropsDefaultValues(),
            socialLinksAppearance: BymoTemplateSpeedDialSocialLinksAppearance.Default,
            ...restProps
        }
    },
    templatePropsMetadata: {
        bioText: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {
            description: 'field.bioText',
        }),
        actionsHeading: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, { aiGenerationStrategy: aiGenerationStrategyExclude() }),
        searchPlaceholder: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, { aiGenerationStrategy: aiGenerationStrategyExclude() }),
        bioTextMaxLines: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        showFullScreenAutoCompleteOnMobile: BymoPagePropsSpecialContentSettingMetadata.Ignored,
    },
    features: [
        'hasActionButton',
        'hasProfileContent',
        'hasVisibleBackground',
        'hasCustomTextualContent',
        'hasActions',        // ✅ Single-page support
        'canBeSkipped',
    ]
})